import { CurrencyPipe, DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { PoI18nService } from '@po-ui/ng-components';
import { Customer } from 'src/app/features/customers/home-customer/customer.model';
import { LiteralsWords } from 'src/app/i18n/literals.model';
import { Prospect } from 'src/app/shared/interfaces/prospect.model';
import { v4 as uuidv4 } from 'uuid';
import { ZipCodePipe } from '../pipe/zipcode.pipe';
import { LiteralsService } from './../services/literals-service';

@Injectable({
  providedIn: 'root'
})
export class Functions {
  literals: LiteralsWords;

  constructor(
    private literalsService: LiteralsService,
    private zipCode: ZipCodePipe,
    private currencyPipe: CurrencyPipe,
    protected datePipe: DatePipe,
    private poI18nService: PoI18nService
  ) {
    this.literals = this.literalsService.getLiteralModule();
  }

  round(value: number, decimalPlaces: number): number {
    const mvArrefat = localStorage.getItem('MV_ARREFAT');

    if (mvArrefat === 'S') {
      const multiplier = Math.pow(10, decimalPlaces);
      const epsilon = 1e-10;
      return Math.round((value + epsilon) * multiplier) / multiplier;
    } else {
      return Number(parseFloat(value.toString()).toFixed(decimalPlaces));
    }
  }

  generateUUID(): string {
    return uuidv4();
  }

  getCurrency(value: number): string {
    return this.currencyPipe.transform(value, 'USD');
  }

  retAscii(i: number, length: number): string {
    let j = 0;
    let newAscii: number;
    let cStr = i + '';

    if (cStr.length > length) {
      if (length === 1) {
        newAscii = 55 + parseInt(cStr.slice(j, 2), 10);
        cStr = String.fromCharCode(newAscii) + cStr.slice(j + 2);
      } else {
        while (j < length) {
          newAscii = 55 + parseInt(cStr.slice(j, 2), 10);
          cStr = String.fromCharCode(newAscii) + cStr.slice(j + 2);
          j = cStr.length;
        }
      }
    } else {
      return ('00' + i).slice(-2);
    }
    return cStr;
  }

  getInvertRetAscii(value: string): number {
    const asciiCodeOfA = 'A'.charCodeAt(0);
    const asciiCodeOf1Value = value.charCodeAt(0);

    if(this.isCharacterNumeric(value.slice(0,1)) && this.isCharacterNumeric(value.slice(1))){
      return parseInt(value);
    } else {
      let diffAscii = asciiCodeOf1Value - asciiCodeOfA;
      diffAscii = diffAscii > 0 ? diffAscii : 0
      const numberOfLetter = 100 + (10 * diffAscii);
      const numberOfValue = this.isCharacterNumeric(value.slice(1)) ? parseInt(value.slice(1)) : 10;
      return numberOfLetter + numberOfValue;
    }
  }

  isCharacterNumeric(char: string): boolean {
    const charCode = char.charCodeAt(0);
    return charCode >= 48 && charCode <= 57;
  }

  getFreightType(freightType: string): string {
    switch (freightType) {
      case 'C':
        return this.literals.str0222;
      case 'F':
        return this.literals.str0223;
      case 'T':
        return this.literals.str0218;
      case 'R':
        return this.literals.str0219;
      case 'D':
        return this.literals.str0220;
      case 'S':
        return this.literals.str0221;
      default:
        return this.literals.str0224;
    }
  }

  getReleaseType(releaseType: string): string {
    switch (releaseType) {
      case '1':
        return this.literals.str0216;
      case '2':
        return this.literals.str0217;
      default:
        return this.literals.str0224;
    }
  }

  transformAddress(entity: Customer | Prospect): string {
    let address: string;
    if (entity.address) {
      address = entity.address + '. ';
    }

    if (entity.zipcode) {
      address += this.zipCode.transform(entity.zipcode) + ' - ';
    }

    if (entity.neighborhood) {
      address += entity.neighborhood + ', ';
    }

    if (entity.city) {
      address += entity.neighborhood ? entity.city : ', ' + entity.city;
    }

    if (entity.uf) {
      address += ' - ' + entity.uf;
    }

    if (entity.countrydescription) {
      address += ', ' + entity.countrydescription;
    }
    return address;
  }

  changePersonType(
    personType: string
  ): { documentLabel: string; nameLabel: string; shortNameLabel: string } {
    const literalsObject = {
      documentLabel: '',
      nameLabel: '',
      shortNameLabel: ''
    };

    if (personType === 'F') {
      literalsObject.documentLabel = this.literals.str0155;
      literalsObject.nameLabel = this.literals.str0156;
      literalsObject.shortNameLabel = this.literals.str0157;
    } else {
      literalsObject.documentLabel = this.literals.str0154;
      literalsObject.nameLabel = this.literals.str0074;
      literalsObject.shortNameLabel = this.literals.str0076;
    }

    return literalsObject;
  }

  returnStringWithRegex(search: string, regex: RegExp): string {
    return search.replace(regex, '');
  }

  transformDateToStringSeparateBySearch(search: string): string {
    const dateSearch = search.split('/');
    const dayNumeric = parseFloat(dateSearch[0]);
    const day = dayNumeric < 10 ? dayNumeric.toString() : dateSearch[0];
    const monthNumeric = parseFloat(dateSearch[1]);
    const month = monthNumeric < 10 ? monthNumeric.toString() : dateSearch[1];

    const dateFormated = new Date(
      +dateSearch[2],
      +month - 1,
      +day
    )
      .toISOString()
      .slice(0, 10);
    return this.datePipe.transform(dateFormated, 'yyyy-M-d');
  }

  transformDateToStringUnionBySearch(search: string): string {
    const dateSearch = search.split('/');

    const day = dateSearch[0].length === 1 ? `0${dateSearch[0]}` : dateSearch[0];
    const month = dateSearch[1].length === 1 ? `0${dateSearch[1]}` : dateSearch[1];

    const dateFormated = new Date(
      +dateSearch[2],
      +month - 1,
      +day
    )
      .toISOString()
      .slice(0, 10);
    return this.datePipe.transform(dateFormated, 'yyyyMMdd');
  }

  transformDateToStringUnionByDate(day: string, month: string, year: string): string {
    const dateFormated = this.returnDateFormated(day, month, year);
    return this.datePipe.transform(dateFormated, 'yyyyMMdd');
  }

  transformDateToStringSeparateByDate(day: string, month: string, year: string): string {
    const dateFormated = this.returnDateFormated(day, month, year);
    return this.datePipe.transform(dateFormated, 'yyyy-MM-dd');
  }

  returnDateFormated(day: string, month: string, year: string): string {
    day = day.length === 1 ? `0${day}` : day;
    month = month.length === 1 ? `0${month}` : month;

    return new Date(
      +year,
      +month - 1,
      +day
    ).toISOString().slice(0, 10);
  }

  filterIsDate(search: string): boolean {
    const regexString = /[a-zA-Z]/g;
    const regexNumber = /^\s*[+-]?(\d+|\.\d+|\d+\.\d+|\d+\.)(e[+-]?\d+)?\s*$/;
    const regexDate =  /^\d{1,2}\/\d{1,2}\/\d{4}/g;

    if (regexNumber.test(search.replace(/\//g, ''))
      && search.indexOf('/') > -1
      && !regexString.test(search)
      && regexDate.test(search)) {
      return true;
    }
    return false;
  }

  maskCurrencyDependingOnLocale(currency: string, decimals: number = 2): string {
    if (navigator.language === 'es-MX') {
      return parseFloat(currency).toLocaleString('en-US', { minimumFractionDigits: decimals });
    }

    return parseFloat(currency).toLocaleString(navigator.language, { minimumFractionDigits: decimals });
  }

  getLocaleCurrencyMask(): string {
    let lang = this.poI18nService.getShortLanguage();

    if (navigator.language === 'es-MX') {
      lang = 'en';
    }

    return lang;
  }

  isDate(str: string): boolean {
    const parts = str.includes('-') ? str.split('-') : str.split('/');
    if (parts.length !== 3) {
      return false;
    }

    let year = parseInt(parts[0]);
    const month = parseInt(parts[1]);
    let day = parseInt(parts[2]);

    if (str.includes('/')) {
      [day, year] = [year, day];
    }

    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return false;
    }

    const date = new Date(year, month - 1, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  }

  strToDate(str: string): Date {
    const parts = str.split('-');
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]);
    const day = parseInt(parts[2]);
    return new Date(year, month - 1, day);
  }

  isVisibleButton(button: string): boolean {
    let isVisible = true;

    if (button === 'effective'){
      isVisible = this.checkVersion('1.11.3');
    } else if(button === 'cancel'){
      isVisible = this.checkVersion('1.11.3');
    }
    return isVisible;
  }

  checkVersion(versionValid: string): boolean{
    const storedVersion = localStorage.getItem('serverVersion');
    if (!storedVersion) {
      return false;
    }
    return this.compareVersions(storedVersion, versionValid) >= 0;
  }

  compareVersions(v1: string, v2: string): number {
    const v1Parts = v1.split('.').map(Number);
    const v2Parts = v2.split('.').map(Number);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const num1 = v1Parts[i] || 0;
      const num2 = v2Parts[i] || 0;

      if (num1 < num2) return -1;
      if (num1 > num2) return 1;
    }
    return 0;
  }

  // format date from '20251231' to '2025-12-31'
  formatDateString(dateStr: string): string {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${year}-${month}-${day}`;
  }

  getBase64ImageFromAssets(path: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = path;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);

        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };

      img.onerror = error => reject(error);
    });
  }

  getValueField(value: number, field: string): number {
    let decimals = 2;
    const fieldsData = localStorage.getItem('fieldsDecimals');

    if (fieldsData) {
      decimals = this.getSizeFieldsDecimal(field);
    }
    return this.round(value, decimals);
  }

  getSizeFieldsDecimal(field: string): number {
    const paramDecimal = localStorage.getItem('MV_PGVDECI') === 'true' ? true : false;
    let size = 2;

    if (paramDecimal) {
      const fieldSizeDecimal = this.getFieldsSchema(field);

      if (fieldSizeDecimal > 0) {
        size = fieldSizeDecimal;
      }

    }
    return size;
  }

  getFieldsSchema(field: string): number {
    let valueSizeDecimal = 2;
    const fieldsData = localStorage.getItem('fieldsDecimals');

    if (!fieldsData) {
      return valueSizeDecimal;
    }

    const fieldsJson = JSON.parse(fieldsData);

    if (fieldsJson && Object.hasOwn(fieldsJson, field)) {
      valueSizeDecimal = fieldsJson[field];
    }

    return valueSizeDecimal;
  }
}
